import React, { createElement, forwardRef, useId } from 'react';
import { ICON_SHAPES } from './icon-shapes';

// Scale the 1.8-unit stroke with the drawing: at 32px it becomes 2.4px.
// Keep state colors and accessible action names on the enclosing control.
const Icon = forwardRef(function Icon({ name, size = '1em', color = 'currentColor', filled = false, className = '', title, ...props }, ref) {
  const cutoutId = `wiw-icon-cutout-${useId().replace(/:/g, '')}`;
  const shapes = ICON_SHAPES[name];
  if (!shapes) throw new Error(`Unknown WIW icon: ${name}`);
  const hasBadge = shapes.some(([, attrs]) => attrs.part === 'badge');
  const slash = shapes.find(([, attrs]) => attrs.part === 'slash');
  const labelled = Boolean(title || props['aria-label'] || props['aria-labelledby']);
  return (
    <svg ref={ref} width={size} height={size} viewBox="0 0 24 24"
      fill={filled && !hasBadge ? color : 'none'} stroke={color} color={color}
      strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
      className={`wiw-icon ${className}`.trim()} aria-hidden={labelled ? undefined : true}
      role={labelled ? 'img' : undefined} focusable="false" {...props} data-icon={name}>
      {title && <title>{title}</title>}
      {slash && <defs>
        {/* Transparent clearance, so the diagonal stays clean on any surface.
            Every row and confirmation has its own mask to avoid ID collisions. */}
        <mask id={cutoutId} x="0" y="0" width="24" height="24" maskUnits="userSpaceOnUse" style={{ maskType: 'luminance' }}>
          <rect width="24" height="24" fill="white" stroke="none" />
          <path d={slash[1].d} fill="none" stroke="black" strokeWidth="4.2" strokeLinecap="round" />
        </mask>
      </defs>}
      {shapes.map(([tag, { part, ...attrs }], index) => createElement(tag, {
        key: index,
        ...attrs,
        ...(part === 'badge' ? { fill: filled ? color : 'none' } : {}),
        ...(part === 'mark' && filled ? { stroke: 'var(--wiw-icon-cutout, #FAF9F6)' } : {}),
        ...(part === 'silhouette' && slash ? { mask: `url(#${cutoutId})` } : {}),
      }))}
    </svg>
  );
});

function drawing(name, defaults = {}) {
  const Component = forwardRef((props, ref) => <Icon name={name} {...defaults} {...props} ref={ref} />);
  Component.displayName = `WiwIcon(${name})`;
  return Component;
}

export { Icon };
export default Icon;
export const HomeIcon = drawing('home');
export const PlusIcon = drawing('plus');
export const PlusCircleIcon = drawing('plusCircle');
export const EventIcon = drawing('event');
export const NewsAddIcon = drawing('newsAdd');
export const RepostIcon = drawing('repost');
export const ChatIcon = drawing('chat');
export const CommentEditIcon = drawing('commentEdit');
export const AccountIcon = drawing('account');
export const BackIcon = drawing('back');
export const ChevronUpIcon = drawing('up');
export const ChevronDownIcon = drawing('down');
export const EyeIcon = drawing('eye');
export const EyeOffIcon = drawing('eyeOff');
export const BanIcon = drawing('ban');
export const ShareIcon = drawing('share');
export const MailIcon = drawing('mail');
export const CopyIcon = drawing('copy');
export const HeartIcon = drawing('heart');
export const HeartFilledIcon = drawing('heart', { filled: true });
export const SettingsIcon = drawing('settings');
export const LoginIcon = drawing('login');
export const LogoutIcon = drawing('logout');
export const UsersIcon = drawing('users');
export const OpinionsIcon = drawing('opinions');
export const GroupIcon = drawing('group');
export const FollowIcon = drawing('follow');
export const FollowedIcon = drawing('followed');
export const UnfollowIcon = drawing('unfollow');
export const ChartIcon = drawing('chart');
export const GlobeIcon = drawing('globe');
export const LocationIcon = drawing('location');
export const MoreIcon = drawing('more');
export const TranslateIcon = drawing('translate');
export const CheckIcon = drawing('check');
export const CheckCircleIcon = drawing('checkCircle');
export const CloseIcon = drawing('close');
export const CloseCircleIcon = drawing('closeCircle');
export const EditIcon = drawing('edit');
export const TrashIcon = drawing('trash');
export const CleanIcon = drawing('clean');
export const MenuIcon = drawing('menu');
export const SendIcon = drawing('send');
export const UndoIcon = drawing('undo');
export const RedoIcon = drawing('redo');
export const PhotoIcon = drawing('photo');
export const BrushIcon = drawing('brush');
export const PaperclipIcon = drawing('paperclip');
export const PenIcon = drawing('pen');
export const MarkerIcon = drawing('marker');
export const EraserIcon = drawing('eraser');
export const CropIcon = drawing('crop');
export const MicrophoneIcon = drawing('microphone');
export const MicrophoneOffIcon = drawing('microphoneOff');
export const VolumeIcon = drawing('volume');
export const VolumeOffIcon = drawing('volumeOff');
export const MicIcon = MicrophoneIcon;
export const CameraIcon = drawing('camera');
export const CameraSwitchIcon = drawing('cameraSwitch');
export const VideoCameraIcon = drawing('videoRecord');
export const PlayIcon = drawing('play');
export const PauseIcon = drawing('pause');
export const RecordIcon = drawing('record');
export const StopIcon = drawing('stop');
export const FullscreenIcon = drawing('fullscreen');
export const FullscreenExitIcon = drawing('fullscreenExit');
export const KeyboardHideIcon = drawing('keyboardHide');
export const AtIcon = drawing('at');
export const SaveIcon = drawing('save');
export const BellIcon = drawing('bell');
export const BellOnIcon = drawing('bellOn');
export const BellOffIcon = drawing('bellOff');
export const FlagIcon = drawing('flag');
export const AlertIcon = drawing('alert');
export const AlertCircleIcon = drawing('alertCircle');
export const SearchIcon = drawing('search');
export const SquareIcon = drawing('square');
export const CheckSquareIcon = drawing('checkSquare');
